import { useEffect, useState } from "react";
import { ArrowUp } from "lucide-react";
import { useFloatingControlGuard } from "@/hooks/useFloatingControlGuard";

/**
 * Small "back to top" control shown on every page after the visitor has
 * scrolled a while. Positioned to avoid the sticky mobile CTA bar and the
 * "Request Service or Assessment" button.
 */
const BackToTopButton = ({ showAfterPx = 900 }: { showAfterPx?: number }) => {
  const [isVisible, setIsVisible] = useState(false);
  const { ref, obstructed } = useFloatingControlGuard<HTMLButtonElement>();

  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        setIsVisible(window.scrollY > showAfterPx);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [showAfterPx]);

  return (
    <button
      ref={ref}
      aria-hidden={!isVisible || obstructed}
      data-floating-control
      type="button"
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      aria-label="Back to top of page"
      className={`fixed right-4 bottom-[84px] md:bottom-6 z-40 flex items-center gap-1.5 rounded-full
                 bg-primary/85 text-primary-foreground backdrop-blur-sm border border-accent/50
                 shadow-[0_8px_24px_-8px_rgba(0,0,0,0.7)] px-3 py-2 text-xs font-semibold
                 hover:bg-primary transition-colors touch-manipulation ${!isVisible || obstructed ? "invisible pointer-events-none" : "visible"}`}
    >
      <ArrowUp className="w-4 h-4 text-accent" aria-hidden="true" />
      <span className="hidden sm:inline">Go to top</span>
    </button>
  );
};

export default BackToTopButton;
